import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  REGISTER_PAGE_SIZE,
  filterRecordsBySupersededVisibility,
  orderRecordRegisterRowsForSavedView,
  paginateRegisterRows,
  resolveShowSupersededRecords,
  showSupersededRecordsParam,
  sortCaseRegisterRows,
  sortRecordRegisterRows,
  sortScanRegisterRows,
  caseRegisterIssueDisplay,
  type CaseRegisterRow,
  type CaseRegisterSortKey,
  type RecordRegisterRow,
  type RecordRegisterSortKey,
  type ScanRegisterRow,
  type ScanRegisterSortKey,
  type SortDirection,
} from '../../domain/safetyCases/workspaceRegisters';
import { SafetyCaseOwnerAssignDialog } from './SafetyCaseOwnerAssignDialog';
import { matchScheduledStatusToWorkflow, resolveCurrentScanTruth } from '../../domain/safetyCases/currentScanTruth';
import type { ScheduledScanStatusRow } from '../../domain/safetyCases/workspaceScheduledScanStatus';
import { filterCasesByControlStage } from '../../domain/safetyCases/workspaceControlStageRollup';
import {
  filterCasesByHazardCategory,
  hazardCategoryFilterParam,
  resolveHazardCategoryFilter,
} from '../../domain/safetyCases/workspaceHazardCategoryRollup';
import type { ControlStage } from '../../domain/safetyCases/workspaceResolvedState';
import {
  applyScanDefinitionScopeToSearchParams,
  applySavedViewToSearchParams,
  filterCasesBySavedView,
  filterRecordsBySavedView,
  filterScansByDefinitionScope,
  filterScansBySavedView,
  resolveSavedView,
  resolveScanDefinitionScope,
  savedViewLabel,
  savedViewParam,
  scanDefinitionScopeParam,
} from '../../domain/safetyCases/workspaceSavedViews';
import {
  scanDefinitionScopeHeading,
} from '../../domain/safetyCases/workspaceScansOrganize';
import { groupCasesByOperationalBucket } from '../../domain/safetyCases/caseOperationalGrouping';
import { caseRowHasNewActivity } from '../../domain/safetyCases/workspacePrimaryCta';
import { formatRecordSignerLabel } from '../../domain/safetyCases/workspaceStatusDimensions';
import { useSearchParams } from 'react-router-dom';
import {
  ScheduledScanStatusPanel,
  type ScheduledScanDefinitionSelection,
} from './ScheduledScanStatusPanel';
import { WorkspaceSavedViewsBar } from './WorkspaceSavedViewsBar';
import {
  CASE_COL_ISSUE,
  CASE_COL_CURRENT_SCAN,
  CASE_COL_CONTROL,
  CASE_COL_OWNER,
  CASE_COL_NEXT_STEP,
  CASE_COL_STATUS,
  RECORDS_EMPTY,
  RECORDS_PORTFOLIO_EMPTY,
  RECORDS_SIGNING_NOTE,
  SAFETY_CASES_EMPTY,
  SAFETY_CASES_EMPTY_HINT,
  SAFETY_CASES_PORTFOLIO_EMPTY,
  SAFETY_SCANS_EMPTY,
  SAFETY_SCANS_PORTFOLIO_EMPTY,
  SCAN_COL_WINDOW,
  SCAN_COL_RESULT,
  SCAN_COL_VERIFICATION,
  CREATE_CASE_LABEL,
  OPEN_SAFETY_CASE_LABEL,
  caseRegisterNextStepDisplay,
  caseScanCountDisplay,
  NO_COMPLETED_SAFETY_SCAN_YET,
  registerEmptyNoun,
  safetyScansCurrentEmptyCopy,
  scanLifecycleMeasurementLabel,
} from '../../domain/safetyCases/safetyCasesWorkspacePresentationCopy';
import { compactCasesLiveSummary } from '../../domain/buyerFacing/conditionDisplayNames';
import {
  buyerScanActionPresentation,
  dedupeBuyerLocationLabel,
} from '../../domain/safetyCases/scanBuyerResultHierarchy';
import {
  canOpenScan,
  groupScansUnderCases,
  type CaseScanGroup,
} from '../../domain/safetyCases/workspaceScanCaseGroups';

export type WorkspaceRegisterKind = 'cases' | 'scans' | 'records';

function formatUpdated(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function registerActionLabel(kind: WorkspaceRegisterKind, row: {
  newActivity?: string;
  actionLabel: string;
  packageAvailable?: boolean;
}): string {
  return row.actionLabel;
}

function CaseActionCell({
  row,
  onCaseAction,
}: {
  row: CaseRegisterRow;
  onCaseAction: (row: CaseRegisterRow) => void;
}) {
  const actionLabel = registerActionLabel('cases', row);
  return (
    <TableCell>
      <Stack spacing={0.5} alignItems="flex-start">
        <Button
          size="small"
          variant="text"
          onClick={() => onCaseAction(row)}
          data-testid={`workspace-register-case-action-${row.id}`}
        >
          {actionLabel}
        </Button>
        {row.actionHelper ? (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={`workspace-register-case-action-helper-${row.id}`}
            sx={{ maxWidth: 220, lineHeight: 1.3 }}
          >
            {row.actionHelper}
          </Typography>
        ) : null}
      </Stack>
    </TableCell>
  );
}

function ScanActionCell({
  row,
  isHistory,
  onScanAction,
  onCreateCase,
}: {
  row: ScanRegisterRow;
  isHistory?: boolean;
  onScanAction: (row: ScanRegisterRow) => void;
  onCreateCase?: (row: ScanRegisterRow) => void;
}) {
  const presentation = row.reportId
    ? buyerScanActionPresentation({
      reportId: row.reportId,
      isHistory,
      needsReview: row.needsReview,
      workflowState: row.workflowState,
    })
    : {
      actionLabel: row.actionLabel,
      actionAriaLabel: row.actionAriaLabel || row.actionLabel,
    };
  return (
    <TableCell>
      <Stack spacing={0.5} alignItems="flex-start">
        {canOpenScan(row) ? (
          <Button
            size="small"
            onClick={() => onScanAction(row)}
            aria-label={presentation.actionAriaLabel}
            data-testid={`workspace-register-scan-action-${row.id}`}
          >
            {presentation.actionLabel}
          </Button>
        ) : null}
        {!row.caseId ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onCreateCase?.(row)}
            data-testid={`workspace-register-scan-create-case-${row.id}`}
          >
            {CREATE_CASE_LABEL}
          </Button>
        ) : null}
      </Stack>
    </TableCell>
  );
}

function SortableHeader<K extends string>({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
}: {
  label: string;
  sortKey: K;
  activeKey: K;
  direction: SortDirection;
  onSort: (key: K) => void;
}) {
  return (
    <TableCell sortDirection={activeKey === sortKey ? direction : false}>
      <TableSortLabel
        active={activeKey === sortKey}
        direction={activeKey === sortKey ? direction : 'asc'}
        onClick={() => onSort(sortKey)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

function RegisterEmpty({
  kind,
  loading,
  portfolioMode = false,
  viewLabel = null,
  definitionScopeName = null,
  onClearFilters,
}: {
  kind: WorkspaceRegisterKind;
  loading: boolean;
  portfolioMode?: boolean;
  viewLabel?: string | null;
  definitionScopeName?: string | null;
  onClearFilters?: () => void;
}) {
  if (loading) {
    return (
      <Stack spacing={1} sx={{ py: 2 }} data-testid={`workspace-register-${kind}-loading`}>
        <Skeleton height={36} />
        <Skeleton height={36} />
        <Skeleton height={36} />
      </Stack>
    );
  }
  if (portfolioMode) {
    const copy = kind === 'cases'
      ? SAFETY_CASES_PORTFOLIO_EMPTY
      : kind === 'scans'
        ? SAFETY_SCANS_PORTFOLIO_EMPTY
        : RECORDS_PORTFOLIO_EMPTY;
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid={`workspace-register-${kind}-empty`}
        sx={{ py: 2 }}
      >
        {copy}
      </Typography>
    );
  }
  const filterParts = [
    viewLabel ? `view “${viewLabel}”` : null,
    definitionScopeName ? `definition “${definitionScopeName}”` : null,
  ].filter(Boolean);
  if (filterParts.length > 0) {
    return (
      <Stack spacing={1} sx={{ py: 2 }} data-testid={`workspace-register-${kind}-empty`}>
        <Typography variant="body2" color="text.secondary">
          No {registerEmptyNoun(kind)} match{' '}
          {filterParts.join(' and ')}.
        </Typography>
        {onClearFilters ? (
          <Box>
            <Button
              size="small"
              variant="outlined"
              onClick={onClearFilters}
              data-testid={`workspace-register-${kind}-clear-filters`}
            >
              Clear filters
            </Button>
          </Box>
        ) : null}
      </Stack>
    );
  }
  const copy = kind === 'cases'
    ? SAFETY_CASES_EMPTY
    : kind === 'scans'
      ? SAFETY_SCANS_EMPTY
      : RECORDS_EMPTY;
  return (
    <Stack spacing={0.5} sx={{ py: 1.5 }} data-testid={`workspace-register-${kind}-empty`}>
      <Typography variant="body2" color="text.secondary">
        {copy}
      </Typography>
      {kind === 'cases' ? (
        <Typography variant="caption" color="text.secondary">
          {SAFETY_CASES_EMPTY_HINT}
        </Typography>
      ) : null}
    </Stack>
  );
}

function LoadMoreBar({
  kind,
  shown,
  total,
  hasMoreLocal,
  canFetchMore,
  loadingMore,
  onLoadMore,
}: {
  kind: WorkspaceRegisterKind;
  shown: number;
  total: number;
  hasMoreLocal: boolean;
  canFetchMore: boolean;
  loadingMore?: boolean;
  onLoadMore: () => void;
}) {
  if (!hasMoreLocal && !canFetchMore) {
    return (
      <Typography variant="caption" color="text.secondary" data-testid={`workspace-register-${kind}-count`}>
        {total === 0 ? '' : `Showing ${shown} of ${total}`}
      </Typography>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pt: 1 }}>
      <Typography variant="caption" color="text.secondary" data-testid={`workspace-register-${kind}-count`}>
        Showing {shown} of {total}{canFetchMore ? '+' : ''}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        data-testid={`workspace-register-${kind}-load-more`}
        disabled={loadingMore}
        onClick={onLoadMore}
      >
        {loadingMore ? 'Loading…' : 'Load more'}
      </Button>
    </Stack>
  );
}

export function WorkspaceRegistersPanel({
  kind,
  siteId,
  portfolioMode = false,
  canViewScheduledScans = false,
  actorIdentity,
  caseRows,
  scanRows,
  recordRows,
  loading,
  error,
  canFetchMore = false,
  loadingMore = false,
  onRetry,
  onLoadMoreFetch,
  onCaseAction,
  onScanAction,
  onCreateCase,
  onRecordAction,
  onRecordReviewActivity,
  onRecordDownload,
  canAssignOwner = false,
  onOwnerAssigned,
}: {
  kind: WorkspaceRegisterKind;
  /** Site scope for Scheduled Scan status (Scans tab). */
  siteId?: string | null;
  /** Org portfolio mode: registers are not populated from the portfolio contract. */
  portfolioMode?: boolean;
  canViewScheduledScans?: boolean;
  actorIdentity?: string | null;
  caseRows: CaseRegisterRow[];
  scanRows: ScanRegisterRow[];
  recordRows: RecordRegisterRow[];
  loading: boolean;
  error?: string | null;
  canFetchMore?: boolean;
  loadingMore?: boolean;
  onRetry?: () => void;
  onLoadMoreFetch?: () => void;
  onCaseAction: (row: CaseRegisterRow) => void;
  onScanAction: (row: ScanRegisterRow) => void;
  /** Navigate to explicit Case create; must not auto-create or auto-admit. */
  onCreateCase?: (row: ScanRegisterRow) => void;
  onRecordAction: (row: RecordRegisterRow) => void;
  onRecordReviewActivity?: (row: RecordRegisterRow) => void;
  onRecordDownload?: (row: RecordRegisterRow) => void;
  canAssignOwner?: boolean;
  onOwnerAssigned?: () => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const savedView = resolveSavedView(searchParams.get(savedViewParam()));
  const activeViewLabel = savedViewLabel(savedView);
  const showSupersededRecords = resolveShowSupersededRecords(
    searchParams.get(showSupersededRecordsParam()),
  );
  const selectedHazardCategory = kind === 'cases'
    ? resolveHazardCategoryFilter(searchParams.get(hazardCategoryFilterParam()))
    : null;
  const selectedScanDefinitionId = kind === 'scans'
    ? resolveScanDefinitionScope(searchParams.get(scanDefinitionScopeParam()))
    : null;
  const [caseSort, setCaseSort] = useState<{ key: CaseRegisterSortKey; direction: SortDirection }>({
    key: 'updated',
    direction: 'desc',
  });
  const [scanSort, setScanSort] = useState<{ key: ScanRegisterSortKey; direction: SortDirection }>({
    key: 'completed',
    direction: 'desc',
  });
  const [recordSort, setRecordSort] = useState<{ key: RecordRegisterSortKey; direction: SortDirection }>({
    key: 'signedDate',
    direction: 'desc',
  });
  const [visibleCount, setVisibleCount] = useState(REGISTER_PAGE_SIZE);
  const [selectedControlStage, setSelectedControlStage] = useState<ControlStage | null>(null);
  const [scanDefinitionScopeName, setScanDefinitionScopeName] = useState<string | null>(null);
  const [nextScheduledLabel, setNextScheduledLabel] = useState<string | null>(null);
  const [scheduledStatusRows, setScheduledStatusRows] = useState<ScheduledScanStatusRow[]>([]);
  const [assignOwnerRow, setAssignOwnerRow] = useState<CaseRegisterRow | null>(null);
  const [expandedScanHistory, setExpandedScanHistory] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setVisibleCount(REGISTER_PAGE_SIZE);
    setSelectedControlStage(null);
  }, [kind]);

  useEffect(() => {
    setVisibleCount(REGISTER_PAGE_SIZE);
  }, [
    selectedControlStage,
    selectedHazardCategory,
    savedView,
    showSupersededRecords,
    selectedScanDefinitionId,
    scanDefinitionScopeName,
  ]);

  useEffect(() => {
    if (!selectedScanDefinitionId) {
      setScanDefinitionScopeName(null);
    }
  }, [selectedScanDefinitionId]);

  const caseRowsWithScanTruth = useMemo(
    () => caseRows.map((row) => {
      const hasCurrent = Boolean(row.currentScanLink)
        || (Boolean(row.currentScan) && row.currentScan !== '—' && row.currentScan !== 'No current Scan');
      if (hasCurrent) return row;
      const truth = resolveCurrentScanTruth({
        hasMaterializedUnsignedScan: false,
        scheduledRow: matchScheduledStatusToWorkflow(scheduledStatusRows, row.workflowId),
      });
      return {
        ...row,
        currentScan: truth.label,
        currentScanEmptyReason: truth.emptyReason || row.currentScanEmptyReason || null,
      };
    }),
    [caseRows, scheduledStatusRows],
  );
  const savedViewCaseRows = useMemo(
    () => filterCasesBySavedView(caseRowsWithScanTruth, savedView, actorIdentity),
    [actorIdentity, caseRowsWithScanTruth, savedView],
  );
  const hazardFilteredCaseRows = useMemo(
    () => filterCasesByHazardCategory(savedViewCaseRows, selectedHazardCategory),
    [savedViewCaseRows, selectedHazardCategory],
  );
  const filteredCaseRows = useMemo(
    () => filterCasesByControlStage(hazardFilteredCaseRows, kind === 'cases' ? selectedControlStage : null),
    [hazardFilteredCaseRows, kind, selectedControlStage],
  );

  const savedViewScanRows = useMemo(
    () => filterScansBySavedView(scanRows, savedView),
    [scanRows, savedView],
  );
  const filteredScanRows = useMemo(
    () => filterScansByDefinitionScope(savedViewScanRows, scanDefinitionScopeName),
    [savedViewScanRows, scanDefinitionScopeName],
  );
  const scanCaseGroups = useMemo(
    () => groupScansUnderCases({
      scanRows: filteredScanRows,
      caseRows: caseRowsWithScanTruth,
      includeZeroScanCases: kind === 'scans' && !savedView && !scanDefinitionScopeName,
    }),
    [caseRowsWithScanTruth, filteredScanRows, kind, savedView, scanDefinitionScopeName],
  );
  const sortedScanGroups = useMemo(() => {
    const sortable = scanCaseGroups.map((group) => ({
      group,
      row: group.current || group.history[0] || null,
    }));
    const withRows = sortable.filter((item) => item.row);
    const withoutRows = sortable.filter((item) => !item.row);
    const sortedRows = sortScanRegisterRows(
      withRows.map((item) => item.row as ScanRegisterRow),
      scanSort.key,
      scanSort.direction,
    );
    const byId = new Map(withRows.map((item) => [item.row!.id, item.group]));
    const ordered = sortedRows
      .map((row) => byId.get(row.id))
      .filter((group): group is CaseScanGroup => Boolean(group));
    return [...ordered, ...withoutRows.map((item) => item.group)];
  }, [scanCaseGroups, scanSort]);
  const definitionScopeHeading = scanDefinitionScopeHeading(scanDefinitionScopeName);
  const siteActiveCaseCount = useMemo(
    () => caseRowsWithScanTruth.filter((row) => row.controlStage !== 'Closed').length,
    [caseRowsWithScanTruth],
  );
  const siteNewActivityCaseCount = useMemo(
    () => caseRowsWithScanTruth.filter((row) => caseRowHasNewActivity(row.newActivity)).length,
    [caseRowsWithScanTruth],
  );
  const siteUnownedCaseCount = useMemo(
    () => caseRowsWithScanTruth.filter((row) => String(row.owner || '').trim().toLowerCase() === 'unassigned').length,
    [caseRowsWithScanTruth],
  );
  const savedViewRecordRows = useMemo(
    () => filterRecordsBySavedView(recordRows, savedView),
    [recordRows, savedView],
  );
  const filteredRecordRows = useMemo(
    () => filterRecordsBySupersededVisibility(savedViewRecordRows, showSupersededRecords),
    [savedViewRecordRows, showSupersededRecords],
  );
  const orderedRecordRows = useMemo(
    () => orderRecordRegisterRowsForSavedView(filteredRecordRows, savedView),
    [filteredRecordRows, savedView],
  );

  const sortedCases = useMemo(
    () => sortCaseRegisterRows(filteredCaseRows, caseSort.key, caseSort.direction),
    [filteredCaseRows, caseSort],
  );
  const sortedRecords = useMemo(
    () => sortRecordRegisterRows(orderedRecordRows, recordSort.key, recordSort.direction),
    [orderedRecordRows, recordSort],
  );

  const casePage = paginateRegisterRows(sortedCases, visibleCount);
  const groupedCaseSections = useMemo(
    () => groupCasesByOperationalBucket(casePage.visible),
    [casePage.visible],
  );
  const scanPage = paginateRegisterRows(sortedScanGroups, visibleCount);
  const recordPage = paginateRegisterRows(sortedRecords, visibleCount);
  const activePage = kind === 'cases' ? casePage : kind === 'scans' ? scanPage : recordPage;
  const scanTableColSpan = 4;

  const handleLoadMore = () => {
    if (activePage.hasMore) {
      setVisibleCount((current) => current + REGISTER_PAGE_SIZE);
      return;
    }
    onLoadMoreFetch?.();
  };

  const handleScanDefinitionSelect = (selection: ScheduledScanDefinitionSelection | null) => {
    const next = new URLSearchParams(searchParams);
    applyScanDefinitionScopeToSearchParams(next, selection?.definitionId ?? null);
    setSearchParams(next, { replace: true });
    setScanDefinitionScopeName(selection?.definitionName ?? null);
  };

  const handleDefinitionScopeResolved = (selection: ScheduledScanDefinitionSelection | null) => {
    if (selectedScanDefinitionId && !selection) {
      const next = new URLSearchParams(searchParams);
      applyScanDefinitionScopeToSearchParams(next, null);
      setSearchParams(next, { replace: true });
      setScanDefinitionScopeName(null);
      return;
    }
    setScanDefinitionScopeName((current) => {
      const nextName = selection?.definitionName ?? null;
      return current === nextName ? current : nextName;
    });
  };

  const clearRegisterFilters = () => {
    const next = new URLSearchParams(searchParams);
    applySavedViewToSearchParams(next, null);
    applyScanDefinitionScopeToSearchParams(next, null);
    setSearchParams(next, { replace: true });
    setScanDefinitionScopeName(null);
    setSelectedControlStage(null);
  };

  const toggleCaseSort = (key: CaseRegisterSortKey) => {
    setCaseSort((current) => (
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'updated' || key === 'due' ? 'desc' : 'asc' }
    ));
  };
  const toggleScanSort = (key: ScanRegisterSortKey) => {
    setScanSort((current) => (
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'completed' ? 'desc' : 'asc' }
    ));
  };
  const toggleRecordSort = (key: RecordRegisterSortKey) => {
    setRecordSort((current) => (
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'signedDate' ? 'desc' : 'asc' }
    ));
  };

  const hiddenSupersededCount = kind === 'records'
    ? savedViewRecordRows.filter((row) => row.recordState === 'Superseded').length
    : 0;

  const toggleShowSupersededRecords = () => {
    const next = new URLSearchParams(searchParams);
    const param = showSupersededRecordsParam();
    if (showSupersededRecords) {
      next.delete(param);
    } else {
      next.set(param, '1');
    }
    setSearchParams(next, { replace: true });
  };

  const subtitle = kind === 'cases'
    ? (portfolioMode ? 'Cross-site registers are not available in org portfolio mode.' : null)
    : kind === 'scans'
      ? (portfolioMode ? 'Cross-site registers are not available in org portfolio mode.' : null)
      : (portfolioMode ? 'Cross-site registers are not available in org portfolio mode.' : null);

  return (
    <Stack spacing={1.5} data-testid={`workspace-register-${kind}`}>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}
      {kind === 'records' && !portfolioMode ? (
        <Typography variant="caption" color="text.secondary">
          {RECORDS_SIGNING_NOTE}
        </Typography>
      ) : null}

      <WorkspaceSavedViewsBar tab={kind} />

      {error ? (
        <Alert
          severity="error"
          action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Retry</Button> : undefined}
        >
          {error}
        </Alert>
      ) : null}

      {kind === 'cases' && !portfolioMode ? (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="workspace-register-cases-headline"
        >
          {compactCasesLiveSummary({
            active: siteActiveCaseCount,
            unowned: siteUnownedCaseCount,
            newActivity: siteNewActivityCaseCount,
          })}
        </Typography>
      ) : null}

      {(!portfolioMode && kind === 'scans') ? (
        <Box sx={{ opacity: 0.9 }} data-testid="workspace-scheduled-scan-status">
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
            Scheduled Scan Status
          </Typography>
          <ScheduledScanStatusPanel
            siteId={siteId}
            canView={canViewScheduledScans}
            selectedDefinitionId={selectedScanDefinitionId}
            onSelectDefinition={handleScanDefinitionSelect}
            onDefinitionScopeResolved={handleDefinitionScopeResolved}
            onNextScheduled={setNextScheduledLabel}
            onStatusRows={setScheduledStatusRows}
          />
        </Box>
      ) : null}

      {kind === 'scans' && !portfolioMode && definitionScopeHeading && scanDefinitionScopeName ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          data-testid="workspace-register-scans-definition-scope"
          sx={{
            border: 1,
            borderColor: 'primary.main',
            borderRadius: 2,
            px: 1.5,
            py: 1,
            bgcolor: 'action.selected',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {definitionScopeHeading}
            </Typography>
            <Typography variant="caption" color="text.secondary" data-testid="workspace-register-scans-definition-scope-name">
              {scanDefinitionScopeName}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleScanDefinitionSelect(null)}
            data-testid="workspace-register-scans-clear-definition-scope"
          >
            Clear
          </Button>
        </Stack>
      ) : null}

      {kind === 'records' && !portfolioMode && hiddenSupersededCount > 0 ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            size="small"
            variant={showSupersededRecords ? 'contained' : 'outlined'}
            data-testid="workspace-register-records-show-superseded"
            onClick={toggleShowSupersededRecords}
          >
            {showSupersededRecords
              ? 'Hide superseded'
              : `Show superseded (${hiddenSupersededCount})`}
          </Button>
          {!showSupersededRecords ? (
            <Typography variant="caption" color="text.secondary">
              Older signed revisions are hidden by default.
            </Typography>
          ) : null}
        </Stack>
      ) : null}

      {loading && activePage.total === 0 ? (
        <RegisterEmpty kind={kind} loading portfolioMode={portfolioMode} />
      ) : activePage.total === 0 ? (
        <>
          <RegisterEmpty
            kind={kind}
            loading={false}
            portfolioMode={portfolioMode}
            viewLabel={activeViewLabel}
            definitionScopeName={kind === 'scans' ? scanDefinitionScopeName : null}
            onClearFilters={
              activeViewLabel || scanDefinitionScopeName
                ? clearRegisterFilters
                : undefined
            }
          />
          <LoadMoreBar
            kind={kind}
            shown={0}
            total={0}
            hasMoreLocal={false}
            canFetchMore={Boolean(canFetchMore)}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
          />
        </>
      ) : (
        <>
          <TableContainer sx={{ width: '100%', border: 1, borderColor: 'divider', borderRadius: 2 }}>
            <Table size="small" stickyHeader data-testid={`workspace-register-${kind}-table`} sx={{ width: '100%' }}>
              {kind === 'cases' ? (
                <>
                  <TableHead>
                    <TableRow>
                      <SortableHeader label={CASE_COL_ISSUE} sortKey="case" activeKey={caseSort.key} direction={caseSort.direction} onSort={toggleCaseSort} />
                      <SortableHeader label={CASE_COL_OWNER} sortKey="owner" activeKey={caseSort.key} direction={caseSort.direction} onSort={toggleCaseSort} />
                      <SortableHeader label={CASE_COL_STATUS} sortKey="controlStage" activeKey={caseSort.key} direction={caseSort.direction} onSort={toggleCaseSort} />
                      <SortableHeader label={CASE_COL_CURRENT_SCAN} sortKey="currentScan" activeKey={caseSort.key} direction={caseSort.direction} onSort={toggleCaseSort} />
                      <TableCell>{CASE_COL_CONTROL}</TableCell>
                      <SortableHeader label={CASE_COL_NEXT_STEP} sortKey="nextAction" activeKey={caseSort.key} direction={caseSort.direction} onSort={toggleCaseSort} />
                      <SortableHeader label="Updated" sortKey="updated" activeKey={caseSort.key} direction={caseSort.direction} onSort={toggleCaseSort} />
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedCaseSections.map((section) => (
                      <React.Fragment key={section.bucket}>
                        <TableRow data-testid={`workspace-register-cases-group-${section.bucket}`}>
                          <TableCell colSpan={8} sx={{ bgcolor: 'action.hover', py: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              {section.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {section.helper}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {section.rows.map((row) => {
                      const issue = caseRegisterIssueDisplay(row);
                      const actionLabel = registerActionLabel('cases', row);
                      const nextStep = row.nextAction;
                      const nextStepDisplay = caseRegisterNextStepDisplay(nextStep, actionLabel);
                      return (
                      <TableRow key={row.id} hover data-testid={`workspace-register-case-row-${row.id}`}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, lineHeight: 1.25 }}
                            title={issue.canonicalTitle || undefined}
                            data-testid={`workspace-register-case-issue-${row.id}`}
                          >
                            {issue.issue}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            data-testid={`workspace-register-case-location-${row.id}`}
                          >
                            {row.siteArea || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.owner}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.statusLabel || row.managementState || row.controlStage}
                            variant="outlined"
                            data-status-dimension="control_stage"
                            title="Status"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.currentMeasurement && row.currentMeasurement !== '—'
                              ? row.currentMeasurement
                              : row.currentScan}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.controlLabel}</TableCell>
                        <TableCell data-testid={`workspace-register-case-next-step-${row.id}`}>
                          <Stack spacing={0.25} alignItems="flex-start">
                            {canAssignOwner && nextStep === 'Assign owner' ? (
                              <Button
                                size="small"
                                variant="text"
                                sx={{ px: 0, minWidth: 0, textTransform: 'none', fontWeight: 700 }}
                                onClick={() => setAssignOwnerRow(row)}
                                data-testid={`workspace-register-case-assign-owner-${row.id}`}
                              >
                                Assign owner
                              </Button>
                            ) : (
                              <Typography variant="body2">{nextStepDisplay || '—'}</Typography>
                            )}
                            {row.ownerUnresolved && nextStep !== 'Assign owner' ? (
                              canAssignOwner ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  sx={{ px: 0, minWidth: 0, textTransform: 'none' }}
                                  onClick={() => setAssignOwnerRow(row)}
                                  data-testid={`workspace-register-case-owner-required-${row.id}`}
                                >
                                  Owner required
                                </Button>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Owner required
                                </Typography>
                              )
                            ) : null}
                          </Stack>
                        </TableCell>
                        <TableCell>{formatUpdated(row.updatedAt)}</TableCell>
                        <CaseActionCell row={row} onCaseAction={onCaseAction} />
                      </TableRow>
                      );
                        })}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </>
              ) : null}

              {kind === 'scans' ? (
                <>
                  <TableHead>
                    <TableRow>
                      <SortableHeader label={SCAN_COL_WINDOW} sortKey="window" activeKey={scanSort.key} direction={scanSort.direction} onSort={toggleScanSort} />
                      <SortableHeader label={SCAN_COL_RESULT} sortKey="result" activeKey={scanSort.key} direction={scanSort.direction} onSort={toggleScanSort} />
                      <SortableHeader label={SCAN_COL_VERIFICATION} sortKey="workflowState" activeKey={scanSort.key} direction={scanSort.direction} onSort={toggleScanSort} />
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scanPage.visible.map((group) => {
                      const locationLabel = dedupeBuyerLocationLabel(group.siteArea);
                      const historyExpanded = Boolean(expandedScanHistory[group.findingId]);
                      const collapseHistory = group.history.length > 1
                        || Boolean(group.current && group.history.length > 0);
                      const visibleHistory = collapseHistory && !historyExpanded ? [] : group.history;
                      const renderScanRow = (scanRow: ScanRegisterRow, options?: { history?: boolean }) => {
                        const lifecycleLabel = scanLifecycleMeasurementLabel(scanRow.trigger);
                        const verificationLabel = scanRow.verificationLabel
                          || (scanRow.workflowState === 'Review required' ? 'Needs review' : '');
                        return (
                          <TableRow
                            key={scanRow.id}
                            hover
                            data-testid={`workspace-register-scan-row-${scanRow.id}`}
                            data-scan-history={options?.history ? 'true' : undefined}
                            sx={options?.history ? { bgcolor: 'action.hover' } : undefined}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: options?.history ? 400 : 700 }}>
                                {scanRow.windowLabel}
                              </Typography>
                              {lifecycleLabel ? (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {lifecycleLabel}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: options?.history ? 600 : 800 }}>
                                  {scanRow.result}
                                </Typography>
                                {scanRow.resultSecondary ? (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {scanRow.resultSecondary}
                                  </Typography>
                                ) : null}
                                {scanRow.resultTertiary ? (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {scanRow.resultTertiary}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {verificationLabel || '—'}
                              </Typography>
                            </TableCell>
                            <ScanActionCell
                              row={scanRow}
                              isHistory={options?.history}
                              onScanAction={onScanAction}
                              onCreateCase={onCreateCase}
                            />
                          </TableRow>
                        );
                      };
                      return (
                        <React.Fragment key={group.findingId}>
                          <TableRow data-testid={`workspace-register-scan-case-group-${group.findingId}`}>
                            <TableCell colSpan={scanTableColSpan} sx={{ bgcolor: 'action.hover', py: 1.25 }}>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent="space-between"
                                spacing={1}
                                data-testid={`workspace-register-scan-case-header-${group.findingId}`}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    {group.title}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {locationLabel}
                                    {group.caseState && group.caseState !== '—' ? ` · ${group.caseState}` : ''}
                                    {` · ${caseScanCountDisplay(String(group.completedScanCount))}`}
                                  </Typography>
                                </Box>
                                {group.caseRow ? (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => onCaseAction(group.caseRow!)}
                                    data-testid={`workspace-register-scan-open-case-${group.findingId}`}
                                  >
                                    {OPEN_SAFETY_CASE_LABEL}
                                  </Button>
                                ) : null}
                              </Stack>
                            </TableCell>
                          </TableRow>
                          {group.current ? renderScanRow(group.current) : (
                            group.completedScanCount === 0 ? (
                              <TableRow data-testid={`workspace-register-scan-zero-${group.findingId}`}>
                                <TableCell colSpan={scanTableColSpan}>
                                  <Typography variant="body2" color="text.secondary">
                                    {NO_COMPLETED_SAFETY_SCAN_YET}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              <TableRow data-testid="workspace-register-scans-current-empty">
                                <TableCell colSpan={scanTableColSpan}>
                                  <Typography variant="body2" color="text.secondary">
                                    {safetyScansCurrentEmptyCopy(nextScheduledLabel)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                          {collapseHistory ? (
                            <TableRow data-testid={`workspace-register-scan-history-${group.findingId}`}>
                              <TableCell colSpan={scanTableColSpan} sx={{ py: 0.75 }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  sx={{ px: 0, minWidth: 0, textTransform: 'none', fontWeight: 700 }}
                                  aria-expanded={historyExpanded}
                                  aria-label={historyExpanded
                                    ? 'Hide prior Safety Scans'
                                    : `Show ${group.history.length} prior Safety Scans`}
                                  onClick={() => setExpandedScanHistory((current) => ({
                                    ...current,
                                    [group.findingId]: !historyExpanded,
                                  }))}
                                  data-testid={`workspace-register-scan-history-toggle-${group.findingId}`}
                                >
                                  {historyExpanded
                                    ? 'Hide prior Safety Scans'
                                    : `Show ${group.history.length} prior Safety Scans`}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ) : group.history.length > 0 ? (
                            <TableRow data-testid={`workspace-register-scan-history-${group.findingId}`}>
                              <TableCell colSpan={scanTableColSpan} sx={{ py: 0.75 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  Prior Scans
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : null}
                          {visibleHistory.map((historyRow) => renderScanRow(historyRow, { history: true }))}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </>
              ) : null}

              {kind === 'records' ? (
                <>
                  <TableHead>
                    <TableRow>
                      <SortableHeader label="Record" sortKey="record" activeKey={recordSort.key} direction={recordSort.direction} onSort={toggleRecordSort} />
                      <SortableHeader label="Safety Case" sortKey="safetyCase" activeKey={recordSort.key} direction={recordSort.direction} onSort={toggleRecordSort} />
                      <SortableHeader label="Revision" sortKey="revision" activeKey={recordSort.key} direction={recordSort.direction} onSort={toggleRecordSort} />
                      <SortableHeader label="Scope" sortKey="scope" activeKey={recordSort.key} direction={recordSort.direction} onSort={toggleRecordSort} />
                      <SortableHeader label="Signed by" sortKey="signedBy" activeKey={recordSort.key} direction={recordSort.direction} onSort={toggleRecordSort} />
                      <SortableHeader label="Signed date" sortKey="signedDate" activeKey={recordSort.key} direction={recordSort.direction} onSort={toggleRecordSort} />
                      <TableCell>State</TableCell>
                      <SortableHeader label="New activity" sortKey="newActivity" activeKey={recordSort.key} direction={recordSort.direction} onSort={toggleRecordSort} />
                      <SortableHeader label="Package" sortKey="package" activeKey={recordSort.key} direction={recordSort.direction} onSort={toggleRecordSort} />
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recordPage.visible.map((row) => {
                      const actionLabel = registerActionLabel('records', row);
                      return (
                      <TableRow key={row.id} hover data-testid={`workspace-register-record-row-${row.id}`}>
                        <TableCell>{row.recordLabel}</TableCell>
                        <TableCell>{row.safetyCase}</TableCell>
                        <TableCell>{row.revisionLabel}</TableCell>
                        <TableCell>{row.scopeLabel}</TableCell>
                        <TableCell data-testid={`workspace-register-record-signer-${row.id}`}>
                          {formatRecordSignerLabel({
                            signedBy: row.signedBy,
                            recordState: row.recordState,
                          })}
                        </TableCell>
                        <TableCell>{formatUpdated(row.signedAt)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.recordState}
                            color={row.recordState === 'Signed' ? 'success' : 'default'}
                            variant={row.recordState === 'Signed' ? 'filled' : 'outlined'}
                            data-status-dimension="record_lifecycle"
                            title="Record status"
                          />
                        </TableCell>
                        <TableCell>{row.newActivity}</TableCell>
                        <TableCell data-testid={`workspace-register-record-package-${row.id}`}>
                          <Stack spacing={0.5} alignItems="flex-start">
                            {row.packageAvailable && onRecordDownload ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onRecordDownload(row)}
                                data-testid={`workspace-register-record-download-${row.id}`}
                              >
                                Download PDF
                              </Button>
                            ) : (
                              <Typography variant="body2" component="span">{row.packageLabel}</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Button
                              size="small"
                              onClick={() => onRecordAction(row)}
                              disabled={!row.reportId || row.revision == null}
                              data-testid={`workspace-register-record-action-${row.id}`}
                            >
                              {actionLabel}
                            </Button>
                            {caseRowHasNewActivity(row.newActivity) && onRecordReviewActivity ? (
                              <Button
                                size="small"
                                onClick={() => onRecordReviewActivity(row)}
                                data-testid={`workspace-register-record-review-activity-${row.id}`}
                              >
                                Review new activity
                              </Button>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </>
              ) : null}
            </Table>
          </TableContainer>

          <LoadMoreBar
            kind={kind}
            shown={activePage.visible.length}
            total={activePage.total}
            hasMoreLocal={activePage.hasMore}
            canFetchMore={Boolean(canFetchMore && !activePage.hasMore)}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
          />
        </>
      )}
      <SafetyCaseOwnerAssignDialog
        open={Boolean(assignOwnerRow?.caseId)}
        safetyCaseId={String(assignOwnerRow?.caseId || '')}
        currentOwner={assignOwnerRow?.ownerUnresolved ? '' : assignOwnerRow?.owner}
        onClose={() => setAssignOwnerRow(null)}
        onSaved={() => onOwnerAssigned?.()}
      />
    </Stack>
  );
}
